import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GripVertical, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import type { Instruction } from '@/api'

type SortableInstructionItemProps = {
  instruction: Instruction
  onEdit: (instruction: Instruction) => void
  onDelete: (instruction: Instruction) => void
  onToggle: (instruction: Instruction, is_active: boolean) => void
}

export function SortableInstructionItem({
  instruction,
  onEdit,
  onDelete,
  onToggle,
}: SortableInstructionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: instruction.uuid })

  const [expanded, setExpanded] = useState(false)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-2 rounded-lg border bg-card p-3 transition-colors ${
        isDragging ? 'shadow-lg ring-2 ring-primary' : 'hover:border-primary/30'
      } ${!instruction.is_active ? 'opacity-60' : ''}`}
    >
      <button
        aria-label={`Reorder ${instruction.title}`}
        className="mt-1.5 shrink-0 cursor-grab touch-none text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="min-w-0 flex-1 break-words text-sm font-semibold">
            {instruction.title}
          </h3>
          <Switch
            aria-label={`Enable ${instruction.title}`}
            checked={instruction.is_active}
            onCheckedChange={(checked) => onToggle(instruction, checked)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Options for ${instruction.title}`}
                className="h-8 w-8 shrink-0"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(instruction)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => onDelete(instruction)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className={`mt-1 block w-full whitespace-pre-line break-words text-left text-sm text-muted-foreground ${expanded ? '' : 'line-clamp-4'}`}
        >
          {instruction.content}
        </button>
      </div>
    </div>
  )
}
